import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { BACKEND_URL } from '../config/constants';

/**
 * Sentry Data Source Name (DSN)
 * https://docs.sentry.io/product/sentry-basics/dsn-explainer/
 */
const SENTRY_DSN =
  'https://38c607cb7bdd49a6b260bea6151cd07d@o1050832.ingest.sentry.io/6033525';

/**
 * @param host the string of host name
 * @returns the prefix of the first part of domain.  if no '-' found, return production
 */
export const getPrefixOfDomainName = (host: string): string => {
  const hostParts = host.split('.');

  // if in docker or local dev
  if (
    host.includes('local.kubit.ai') ||
    host.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/) // or any IPv4-like address
  ) {
    return 'dev';
  }

  if (hostParts.length > 1) {
    // if env is abc-name, return the abc part. Else consider it as production
    if (hostParts[0].includes('-') && hostParts[0].split('-').length > 1) {
      return hostParts[0].split('-')[1];
    } else {
      return 'production';
    }
  }

  return 'dev';
};
/**
 * Returns Sentry environment based on parsing of the location hostname
 *
 * @param host host from window.location.host
 */
const getEnvFromHost = (host: string): string => {
  // this is critical code which runs _before_ initialization of error monitoring, make sure no matter what there are no
  // errors that could break the app when running this code
  try {
    const res = getPrefixOfDomainName(host);
    return res;
  } catch (e) {
    console.error(
      `Error during parsing of "${host}" to determine Sentry environment`,
      e,
    );
    return 'production';
  }
};

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
// https://github.com/tc39/proposal-regex-escaping/issues/37
const escapeRegExp = (str: string) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

export const initSentry = () => {
  const env = getEnvFromHost(window.location.hostname);
  if (env !== 'dev') {
    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        new Integrations.BrowserTracing({
          tracingOrigins: [/^\//, new RegExp('^' + escapeRegExp(BACKEND_URL))],
        }),
      ],
      environment: env,

      // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
      // Tune down if it becomes too expensive
      tracesSampleRate: 1.0,
    });
  } else {
    console.info('Sentry is disabled on dev environments');
  }
};

/**
 * Exported private members for testing purpose only
 */
export const visibleForTesting = {
  getEnvFromHost,
};
